import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React__default, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Manager, Reference } from 'react-popper';
import { classnames } from '../../../utils/classnames.js';
import Popper from '../Popper/Popper.js';
import TrapFocus from '../TrapFocus/TrapFocus.js';
import SelectMenu from './Select.Menu.js';
import useHasFocus from './useHasFocus.js';
import './Select.scss.js';
import { SelectContext } from './Select.context.js';
import InputChipsBase from '../InputChipsBase/InputChipsBase.js';
import AllClose from '../../../assets/icons/resultDir/AllClose.js';
import ArrowsChevronDown from '../../../assets/icons/resultDir/ArrowsChevronDown.js';

var Select = function Select(_ref) {
  var options = _ref.options,
      propsOnChange = _ref.onChange,
      _ref$invalid = _ref.invalid,
      invalid = _ref$invalid === void 0 ? false : _ref$invalid,
      onSearch = _ref.onSearch,
      propsValues = _ref.values,
      _ref$multiselect = _ref.multiselect,
      multiselect = _ref$multiselect === void 0 ? false : _ref$multiselect,
      _ref$multiline = _ref.multiline,
      multiline = _ref$multiline === void 0 ? false : _ref$multiline,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'base' : _ref$variant,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$readOnly = _ref.readOnly,
      readOnly = _ref$readOnly === void 0 ? variant === 'menu' : _ref$readOnly,
      _ref$maxOptions = _ref.maxOptions,
      maxOptions = _ref$maxOptions === void 0 ? undefined : _ref$maxOptions,
      _ref$preloader = _ref.preloader,
      preloader = _ref$preloader === void 0 ? false : _ref$preloader,
      _ref$clearOnSelect = _ref.clearOnSelect,
      clearOnSelect = _ref$clearOnSelect === void 0 ? true : _ref$clearOnSelect,
      clearHook = _ref.clearHook,
      _ref$clearable = _ref.clearable,
      clearable = _ref$clearable === void 0 ? !readOnly : _ref$clearable,
      isAsync = _ref.isAsync,
      infinityScrollProps = _ref.infinityScrollProps,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? variant === 'menu' ? 'bottom-end' : 'bottom' : _ref$position,
      endAdornment = _ref.endAdornment,
      startAdornment = _ref.startAdornment,
      onScroll = _ref.onScroll,
      dropdownMaxWidth = _ref.dropdownMaxWidth,
      containerRef = _ref.containerRef,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? 'white' : _ref$backgroundColor,
      _ref$menuVariantSize = _ref.menuVariantSize,
      menuVariantSize = _ref$menuVariantSize === void 0 ? 'm' : _ref$menuVariantSize;
  var id = useMemo(function () {
    return Select.id++;
  }, []);
  var inputRef = useRef(null);
  var popperRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showDropdown = _useState2[0],
      setShowDropdown = _useState2[1];

  var toggleRef = useRef(null);
  var firstElementPosition = useRef(0);
  var refFocused = useHasFocus(inputRef);
  var focused = refFocused || showDropdown;

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      valueState = _useState4[0],
      setValueState = _useState4[1];

  var selectValues = propsValues !== null && propsValues !== void 0 ? propsValues : valueState;

  var _useState5 = useState(function () {
    return selectValues.length > 0 && !multiselect ? selectValues[0].label : '';
  }),
      _useState6 = _slicedToArray(_useState5, 2),
      inputValue = _useState6[0],
      setInputValue = _useState6[1];

  var setSelectValues = function setSelectValues(next) {
    // uncontrolled
    if (!propsValues) {
      setValueState(next);
    }

    propsOnChange === null || propsOnChange === void 0 ? void 0 : propsOnChange(next);
  };
  /** Очистка селекта */


  var onClear = function onClear() {
    setInputValue('');

    if (!multiselect) {
      setSelectValues([]);
    }

    onSearch && onSearch('');
  };

  var onClearSelectedValues = function onClearSelectedValues() {
    setSelectValues([]);
  };

  var onClose = useCallback(function () {
    var _a;

    if (showDropdown) {
      setShowDropdown(false);
      (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  }, [showDropdown]);
  var onOpen = useCallback(function () {
    if (!disabled) {
      setShowDropdown(true);
    }
  }, [disabled]);
  var hanldeInputClick = useCallback(function () {
    if (!disabled) {
      setShowDropdown(!showDropdown);
    }
  }, [disabled, showDropdown]);

  var onReset = function onReset(e) {
    e.stopPropagation();
    onClear();
    onClearSelectedValues();
    onOpen();
  }; // -------------------------------------------------------------------------------------------------------------------


  useEffect(function () {
    setInputValue(selectValues.length > 0 && !multiselect ? selectValues[0].label : '');
  }, [selectValues]);
  useEffect(function () {
    if (!focused) {
      setInputValue('');
    }
  }, [focused]);
  /** Очистка при изменении извне через clearHook */

  useEffect(function () {
    if (clearHook === undefined) {
      return;
    }

    if (!multiselect) {
      setSelectValues([]);
    }

    setInputValue('');
    onSearch && onSearch('');
  }, [clearHook]); // -------------------------------------------------------------------------------------------------------------------

  /** Поиск в селекте */

  var onSelectSearch = function onSelectSearch(e) {
    setInputValue(e.target.value);
  };

  useEffect(function () {
    if (readOnly) {
      return;
    }

    if (onSearch) {
      onSearch(inputValue);
      return;
    }

    var filtered = options.filter(function (o) {
      return o.label.toLowerCase().includes(inputValue.toLowerCase());
    });
    setFilteredOptions(filtered);
  }, [inputValue]); // -------------------------------------------------------------------------------------------------------------------

  useEffect(function () {
    if (clearOnSelect) {
      setInputValue('');
    }

    if (multiselect && selectValues.length === 1 && toggleRef.current) {
      toggleRef.current.getBoundingClientRect().y = firstElementPosition.current;
    }
  }, [multiselect, selectValues]);

  var onValueChange = function onValueChange(option) {
    var result = undefined;

    if (multiselect) {
      var index = selectValues.findIndex(function (o) {
        return option.value === o.value;
      });

      if (index >= 0) {
        result = selectValues.filter(function (_, i) {
          return i !== index;
        });
      } else {
        if (typeof maxOptions === 'number' ? selectValues.length < maxOptions : true) {
          result = [].concat(_toConsumableArray(selectValues), [option]);
        }
      }
    } else {
      result = [option];
    }

    if (result) {
      setSelectValues(result);
    }
  }; // -------------------------------------------------------------------------------------------------------------------


  var _useState7 = useState([]),
      _useState8 = _slicedToArray(_useState7, 2),
      filteredOptions = _useState8[0],
      setFilteredOptions = _useState8[1];

  useEffect(function () {
    setFilteredOptions(options);
  }, [options]); // -------------------------------------------------------------------------------------------------------------------

  var handleOptionClick = function handleOptionClick(option) {
    onValueChange(option);

    if (!multiselect) {
      setInputValue(clearOnSelect ? '' : option.label);
      setShowDropdown(false);

      if (toggleRef.current) {
        toggleRef.current.focus();
      }
    } else {
      setInputValue('');
    }
  }; // -------------------------------------------------------------------------------------------------------------------


  var handleInputKeyDown = function handleInputKeyDown(event) {
    var openKeys = ['ArrowDown', 'Enter'];

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }

    if (event.key === 'Tab') {
      if (showDropdown) {
        event.preventDefault();

        if (popperRef.current) {
          var element = popperRef.current.querySelector('li:not([aria-disabled])');

          if (element) {
            element.focus();
          }
        }
      }
    }

    if (openKeys.includes(event.code)) {
      event.preventDefault();

      if (!showDropdown) {
        onOpen();
      }

      setTimeout(function () {
        if (popperRef.current) {
          var _element = popperRef.current.querySelector('li:not([aria-disabled])');

          if (_element) {
            _element.focus();
          }
        }
      });
    }
  };

  var handleClick = function handleClick(event) {
    if (!readOnly && showDropdown) {
      event.stopPropagation();
    }
  };

  var inputEmbedded = multiselect && selectValues.length !== 0;
  var inputJsx = /*#__PURE__*/React__default.createElement("input", {
    ref: inputRef,
    autoSave: "false",
    autoComplete: "off",
    "data-testid": "rf-select__input",
    className: classnames('rf-select__input', multiselect && 'rf-select__input--multiselect', inputEmbedded && 'rf-select__input_embedded', disabled && 'rf-select__input--disabled', !multiselect && !!selectValues.length && 'rf-select__input--selected'),
    onChange: onSelectSearch,
    onKeyDown: handleInputKeyDown,
    onClick: handleClick,
    value: inputValue,
    disabled: disabled,
    readOnly: readOnly,
    placeholder: multiselect ? selectValues.length ? '' : placeholder : selectValues.length ? selectValues[0].label : placeholder,
    "data-v-6e52f9c7": ""
  });
  var tagsJSX = multiselect && selectValues.length > 0 && /*#__PURE__*/React__default.createElement(InputChipsBase, {
    chips: selectValues,
    multiline: multiline,
    disabled: disabled,
    hideAll: focused && !multiline,
    onContainerClick: onOpen,
    onChipRemove: onValueChange,
    "data-v-6e52f9c7": ""
  }, !readOnly && inputJsx); // -------------------------------------------------------------------------------------------------------------------

  var closeButton = !disabled && clearable && selectValues.length > 0 && /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "rf-select__action",
    onClick: onReset,
    "aria-label": "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(AllClose, {
    "data-v-6e52f9c7": ""
  }));

  var onChevronClick = function onChevronClick(e) {
    e.stopPropagation();
    e.preventDefault();
    setShowDropdown(function (state) {
      return !state;
    });
  };

  var chevronButton = !disabled && /*#__PURE__*/React__default.createElement("div", {
    "data-testid": "rf-select__chevron",
    className: classnames('rf-select__action', showDropdown && 'rf-select__action--rotate'),
    onClick: onChevronClick,
    "aria-label": showDropdown ? 'Скрыть меню' : 'Раскрыть меню',
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(ArrowsChevronDown, {
    color: variant === 'menu' ? '#fff' : '',
    "data-v-6e52f9c7": ""
  }));
  var startAdornmentIcon = startAdornment && variant !== 'tag' ? /*#__PURE__*/React__default.createElement("div", {
    className: "rf-select__adornment rf-select__adornment--start",
    "data-v-6e52f9c7": ""
  }, startAdornment) : null;
  var endAdornmentIcon = variant !== 'tag' ? /*#__PURE__*/React__default.createElement("div", {
    className: "rf-select__adornment rf-select__adornment--end",
    "data-v-6e52f9c7": ""
  }, endAdornment, " ", closeButton, " ", chevronButton) : null; // -------------------------------------------------------------------------------------------------------------------

  var isTagVariant = variant === 'tag';
  var openClass = showDropdown ? 'rf-select__wrapper--open' : '';
  var getWidthDropdown = useCallback(function () {
    var _a;

    return dropdownMaxWidth || ((_a = toggleRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width);
  }, [dropdownMaxWidth]); // -------------------------------------------------------------------------------------------------------------------

  var contextValue = useMemo(function () {
    return {
      query: inputValue
    };
  }, [inputValue]);
  return /*#__PURE__*/React__default.createElement(SelectContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React__default.createElement(Manager, {
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('rf-select', multiselect && 'rf-select__multi'),
    ref: toggleRef,
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(Reference, {
    "data-v-6e52f9c7": ""
  }, function (referenceProps) {
    return /*#__PURE__*/React__default.createElement("div", _extends({}, referenceProps, {
      "data-testid": "rf-select",
      className: classnames(openClass, 'rf-select__wrapper', focused && 'rf-select__wrapper--focus', invalid && 'rf-select__wrapper--invalid', disabled && 'rf-select__wrapper--disabled', "rf-select__wrapper--variant--".concat(variant), "rf-select__wrapper--size--".concat(menuVariantSize), "rf-select__wrapper--background--".concat(backgroundColor)),
      onClick: hanldeInputClick,
      "data-v-6e52f9c7": ""
    }), startAdornmentIcon, /*#__PURE__*/React__default.createElement("div", {
      className: "rf-select__container",
      "data-v-6e52f9c7": ""
    }, tagsJSX, !inputEmbedded && inputJsx), endAdornmentIcon);
  }), /*#__PURE__*/React__default.createElement(Popper, {
    show: showDropdown || preloader,
    popperRef: popperRef,
    toggleRef: toggleRef,
    containerRef: containerRef,
    strategy: "fixed",
    onClose: onClose,
    position: position,
    style: {
      maxWidth: isTagVariant ? 'auto' : getWidthDropdown(),
      width: isTagVariant ? 'auto' : '100%'
    },
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(TrapFocus, {
    open: true,
    disableAutoFocus: !readOnly,
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(SelectMenu, {
    options: filteredOptions,
    selected: selectValues,
    multiselect: multiselect,
    selectId: id,
    menuVariantSize: menuVariantSize,
    variant: variant,
    isAsync: isAsync,
    infinityScrollProps: infinityScrollProps,
    query: inputValue,
    preloader: preloader,
    onOptionClick: handleOptionClick,
    onSearch: onSearch,
    onClose: onClose,
    onScroll: onScroll,
    inputRef: inputRef,
    readOnly: readOnly,
    "data-v-6e52f9c7": ""
  }))))));
};

Select.id = 0;

export { Select as default };
